<script setup lang="ts">
import { computed, h, onBeforeMount } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@/store/authStore"

import type { MenuOption } from "naive-ui"
import { NBadge, NCard, NMenu } from "naive-ui"

import { BellIcon, LogoutIcon, PencilICon, PersonIcon } from "@/components/icons"
import { useNotificationStore } from "@/store/notificationsStore"
import { AuthRouteNameEnum } from "@/shared/enums/routes/auth.enum.ts"
import { UserRouteNameEnum } from "@/shared/enums/routes/user.enum.ts"

const emit = defineEmits<{
		(e: "addScreen"): void;
	}>();

	const { t } = useI18n()
	const authStore = useAuthStore();
	const notificationStore = useNotificationStore();

	const route = useRoute();
	const router = useRouter();

	// Computed so menu re-renders when the locale changes at runtime.
	const menuOptions = computed<MenuOption[]>(() => [
		{
			label: t("sidebar.general"),
			key: UserRouteNameEnum.profileGeneral,
			icon: PersonIcon,
		},
		{
			label: t("sidebar.notifications"),
			key: UserRouteNameEnum.profileNotifications,
			icon: BellIcon,
			hasBadge: true,
		},
		{
			label: t("sidebar.edit_profile"),
			key: UserRouteNameEnum.profileEdit,
			icon: PencilICon,
		},
		{
			label: t("sidebar.logout"),
			key: "user.profile.logout",
			icon: LogoutIcon,
		},
	]);
	const renderMenuLabel = (option: MenuOption) => {
		if (option.hasBadge) {
			return h("div", { class: "menu-custom-label" }, [
				option.label as string,
				h(NBadge, {
					show: Boolean(notificationStore.unreadNotifications),
					class: "menu-custom-label__badge",
					max: 99,
					type: "error",
					value: notificationStore.unreadNotifications,
				}),
			]);
		}
		return h("div", { class: "menu-custom-label" }, { default: () => option.label as string });
	};

	const generateNodeProps = (option: MenuOption) => {
		if (option.key === "user.profile.logout") return { class: "n-menu-item--danger" };
		return {};
	};

	const logout = (): void => {
		authStore.logout();
		router.push({ name: AuthRouteNameEnum.login });
	};

	const menuUpdated = (key: string, item: MenuOption) => {
		if (route.name === key) return;
		if (key === "user.profile.logout") {
			logout();
			return;
		}
		router.push({ name: key });
	};

	onBeforeMount(() => {});
</script>

<template>
	<n-card :bordered="false" class="user-profile-sidebar">
		<n-menu
			:indent="16"
			:node-props="generateNodeProps"
			@update:value="menuUpdated"
			:render-label="renderMenuLabel"
			:value="String(route.name)"
			:options="menuOptions" />
	</n-card>
</template>

<style scoped lang="scss">
	.user-profile-sidebar {
		--n-color: var(--bg-primary) !important;
		border-radius: 0.8rem;

		:deep(.n-card__content) {
			padding: 2rem;
		}
	}
	:deep(.n-menu .n-menu-item) {
		--n-item-height: 5.6rem;
		--n-item-color-hover: var(--bg-secondary);
		--n-item-icon-color: var(--gs-300);
		--n-item-text-color: var(--gs-300);
		--n-item-icon-color-hover: var(--gs-200);
		--n-item-text-color-hover: var(--gs-200);
	}
	:deep(.n-menu .n-menu-item-content::before) {
		--n-border-radius: 0.8rem;
		left: 0;
		right: 0;
	}
	:deep(.menu-custom-label) {
		@include flex(1.6rem, row, space-between, center);
		@include getFont("h5");
	}
	:deep(.n-menu .n-menu-item--danger) {
		--n-item-color-hover: var(--bg-red) !important;
		--n-item-text-color: var(--red) !important;
		--n-item-icon-color: var(--red) !important;
		--n-item-text-color-hover: var(--red) !important;
		--n-item-icon-color-hover: var(--red) !important;
	}
	:deep(.menu-custom-label__badge .n-badge-sup) {
		@include getFont("h6");

		--n-color: var(--red);

		height: 2.4rem;
		line-height: 2rem;
		border-radius: 0.6rem;
		color: var(--bg-primary);
	}
</style>
@/components/icons
